import { COLORS, marginMd } from 'assets/styled';
import { respondTo } from 'assets/styled/breakpoints';
import { hoverLineIn } from 'assets/styled/commonStyles';
import styled from 'styled-components';
import { css } from 'styled-components';

interface StyledProps {
  hasImages: boolean;
  opacityIndex: number;
  opacityCoefficient: number;
  disabled?: boolean;
  isActiveDiscountType?: boolean;
}

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  ${(props: StyledProps) =>
    props.opacityIndex !== undefined &&
    css`
      margin-top: 20rem;
      opacity: ${1 - (props.opacityIndex * props.opacityCoefficient + 0.2)};
    `}
`;

export const NameLabel = styled.label`
  display: flex;
  font-weight: 400;
  font-size: 24rem;
  line-height: 32rem;
  color: ${COLORS.BLACK};

  ${respondTo.smallTablet`
    font-size: 20rem;
  `}
`;

export const FirstRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 30rem;

  ${respondTo.smallTablet`
    flex-direction: column;
    align-items: flex-start;
    gap: 10rem;
  `}

  ${respondTo.bigPhone`
    align-items: center;
  `}
`;

export const ImagesConfigurationWrapper = styled.div`
  display: flex;
  gap: 30rem;

  ${respondTo.smallTablet`
    flex-direction: column;
  `}

  ${respondTo.bigPhone`
    gap: 10rem;
  `}
`;

export const GridPriceWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
`;

export const Grid = styled.div`
  display: grid;
  grid-row-gap: 20rem;
  grid-column-gap: 50rem;
  ${(props: StyledProps) =>
    props.hasImages
      ? css`
          grid-template-columns: repeat(3, minmax(240rem, auto));
        `
      : css`
          grid-template-columns: repeat(4, minmax(240rem, auto));
        `}

  ${respondTo.smallLaptop`
    gap: 20rem;
  `}

    @media screen and (max-width: 1150px) {
    gap: 20rem 40rem;
    ${(props: StyledProps) =>
      props.hasImages
        ? css`
            grid-template-columns: repeat(2, minmax(220rem, auto));
          `
        : css`
            grid-template-columns: repeat(3, minmax(220rem, auto));
          `}
  }

  ${respondTo.midTablet`
    gap: 20rem;
    ${(props: StyledProps) =>
      props.hasImages
        ? css`
            grid-template-columns: repeat(2, minmax(200rem, auto));
          `
        : css`
            grid-template-columns: repeat(3, minmax(200rem, auto));
          `}
  `}

  ${respondTo.smallTablet`
    ${(props: StyledProps) =>
      props.hasImages
        ? css`
            grid-template-columns: repeat(2, minmax(200rem, auto));
          `
        : css`
            grid-template-columns: repeat(2, minmax(200rem, auto));
          `}
  `}

  ${respondTo.bigPhone`
    ${(props: StyledProps) =>
      props.hasImages
        ? css`
            grid-template-columns: repeat(1, minmax(100%, auto));
          `
        : css`
            grid-template-columns: repeat(1, minmax(100%, auto));
          `}
  `}
`;

export const LabelKey = styled.label`
  font-weight: 600;
  font-size: 15rem;
  color: ${COLORS.BLACK};
`;

export const LabelValue = styled.label<{ isIndicative?: boolean }>`
  font-weight: 300;
  font-size: 15rem;
  line-height: 21rem;
  color: ${COLORS.BLACK};
  text-align: right;
`;

export const RowSpaceBetween = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10rem;
`;

export const LabelKeyWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 5rem;
`;

export const HorizontalLine = styled.div`
  height: 1rem;
  background: ${COLORS.BORDER};
  margin-bottom: ${marginMd};
  ${(props: StyledProps) =>
    props.hasImages &&
    css`
      margin-top: 10rem;
    `}
`;

export const BottomWrapper = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  gap: 30rem;
  align-items: flex-end;
  justify-content: flex-end;
  padding: 20rem 0;

  ${respondTo.smallTablet`
    flex-wrap: wrap;
  `}

  ${respondTo.smallPhone`
    flex-direction: column;
    gap: 10rem;
    padding: 10rem 0;
  `}
`;

export const DiscountWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 5rem;
  width: 100%;
  max-width: 250rem;
`;

export const DiscountType = styled.div`
  display: flex;
  align-items: center;
  gap: 20rem;
`;

export const DiscountTypeLabel = styled.span`
  display: block;
  font-size: 16rem;
  color: ${COLORS.BLACK};
  cursor: pointer;
  ${hoverLineIn}

  ${(props: StyledProps) =>
    props.isActiveDiscountType &&
    css`
      color: ${COLORS.GREEN};
    `}
`;

export const DiscountInput = styled.div`
  width: 100%;
`;

export const QuantityWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 5rem;
`;

export const QuantityLabel = styled.span`
  display: block;
  font-size: 16rem;
  color: ${COLORS.BLACK};
`;

export const PriceWrapper = styled.div`
  height: 100%;
  display: flex;
  align-items: flex-end;
  gap: 10rem;
  flex: 0;
  justify-content: flex-end;
`;

export const PriceLabel = styled.label`
  text-align: end;
  font-weight: 400;
  font-size: 22rem;
  line-height: 40rem;
  color: ${COLORS.BLACK};
  min-width: 170rem;

  ${respondTo.smallTablet`
    font-size: 20rem;
    min-width: 130rem;
  `}

  ${respondTo.bigPhone`
    font-size: 18rem;
    min-width: 115rem;
  `}
`;

export const GrossPriceValue = styled.label`
  font-weight: 600;
  font-size: 22rem;
  line-height: 40rem;
  color: ${COLORS.BLACK};
  text-decoration: line-through;
  white-space: nowrap;

  ${respondTo.bigTablet`
    font-size: 20rem;
  `}

  ${respondTo.smallTablet`
    font-size: 18rem;
  `}

  ${respondTo.bigPhone`
    font-size: 16rem;
  `}
`;

export const NetPriceValue = styled.label`
  color: ${COLORS.GREEN};
  font-weight: 600;
  font-size: 22rem;
  line-height: 40rem;
  white-space: nowrap;

  ${respondTo.smallTablet`
    font-size: 20rem;
  `}

  ${respondTo.bigPhone`
    font-size: 18rem;
  `}
`;

export const EditIconLabelWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 3rem;
  justify-content: space-between;
  transition: transform ease-in-out 50ms;
`;

export const RemoveIconLabelWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 5rem;
  justify-content: space-between;
  transition: transform ease-in-out 50ms;
`;

export const DuplicateIconLabelWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 5rem;
  justify-content: space-between;
  transition: transform ease-in-out 50ms;
`;

export const RightPart = styled.div`
  display: flex;
  gap: 20rem;

  ${respondTo.smallTablet`
    gap: 20rem;
  `}
`;

export const LeftPart = styled.div`
  display: flex;
  align-items: center;
  gap: 10rem;
`;

export const QuantityValue = styled.label`
  font-size: 18rem;
  color: ${COLORS.GREEN};
`;

export const LinkedOrderLabel = styled.label`
  font-size: 16rem;
  color: ${COLORS.BLACK};
`;

export const LinkedOrderValue = styled.label`
  font-size: 16rem;
  font-weight: 500;
  color: ${COLORS.GREEN};
`;
